import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { auth, googleProvider } from '../firebase';

const SESSION_STORAGE_KEY = 'procureflow_google_workspace_session';
const FOLDER_CACHE_KEY_PREFIX = 'procureflow_drive_folder_';

interface StoredWorkspaceSession {
  accessToken: string;
  email?: string;
  displayName?: string;
  savedAt: number;
  expiresAt: number;
  entityId?: string;
}

// In-memory token storage (hydrated from storage on initialization)
let cachedAccessToken: string | null = null;
let cachedUserEmail: string | null = null;

// Hydrate from storage on module load
const hydrateTokenFromStorage = (): string | null => {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY) || localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const session: StoredWorkspaceSession = JSON.parse(raw);
    
    // Check if token is still valid (with a 2-minute safety buffer)
    if (session.expiresAt && session.expiresAt > Date.now() + 120000) {
      cachedAccessToken = session.accessToken;
      cachedUserEmail = session.email || null;
      return cachedAccessToken;
    } else {
      // Expired
      clearWorkspaceSession();
      return null;
    }
  } catch (err) {
    console.warn('Could not hydrate Google Workspace session:', err);
    return null;
  }
};

// Initial hydration attempt
hydrateTokenFromStorage();

export const setCachedToken = (token: string | null, email?: string, entityId?: string) => {
  cachedAccessToken = token;
  if (email) cachedUserEmail = email;
  
  if (token) {
    const session: StoredWorkspaceSession = {
      accessToken: token,
      email: email || cachedUserEmail || undefined,
      savedAt: Date.now(),
      expiresAt: Date.now() + 3600 * 1000, // 1 hour standard OAuth token lifetime
      entityId
    };
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
      localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
    } catch (_) {}
  } else {
    clearWorkspaceSession();
  }
};

export const clearWorkspaceSession = () => {
  cachedAccessToken = null;
  cachedUserEmail = null;
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    localStorage.removeItem(SESSION_STORAGE_KEY);
  } catch (_) {}
};

export const getCachedToken = (): string | null => {
  if (cachedAccessToken) return cachedAccessToken;
  return hydrateTokenFromStorage();
};

export const getConnectedUserEmail = (): string | null => {
  if (cachedUserEmail) return cachedUserEmail;
  hydrateTokenFromStorage();
  return cachedUserEmail;
};

/**
 * Initiates the Google sign-in / re-authentication flow to retrieve a fresh OAuth access token.
 */
export const connectWorkspace = async (currentEntityId?: string): Promise<string | null> => {
  try {
    // Add all scopes required for Google Workspace APIs on a dedicated provider instance
    // so we don't pollute the global authentication provider
    const workspaceProvider = new GoogleAuthProvider();
    const scopes = [
      'https://www.googleapis.com/auth/documents',
      'https://www.googleapis.com/auth/drive.file',
      'https://www.googleapis.com/auth/forms.body',
      'https://www.googleapis.com/auth/spreadsheets',
      'https://www.googleapis.com/auth/tasks',
      'https://www.googleapis.com/auth/contacts.readonly',
      'https://www.googleapis.com/auth/gmail.send',
      'https://www.googleapis.com/auth/gmail.readonly',
      'https://www.googleapis.com/auth/drive.readonly',
      'https://www.googleapis.com/auth/calendar'
    ];

    scopes.forEach(scope => workspaceProvider.addScope(scope));

    const result = await signInWithPopup(auth, workspaceProvider);
    const credential = GoogleAuthProvider.credentialFromResult(result);
    if (!credential?.accessToken) {
      throw new Error('Failed to retrieve access token from Google sign-in.');
    }

    const email = result.user?.email || undefined;
    setCachedToken(credential.accessToken, email, currentEntityId);
    return credential.accessToken;
  } catch (error: any) {
    if (
      error?.code === 'auth/popup-closed-by-user' ||
      error?.code === 'auth/cancelled-popup-request' ||
      (typeof error?.message === 'string' && error.message.includes('popup-closed-by-user'))
    ) {
      console.warn('Google Workspace sign-in popup was closed by user.');
      return null;
    }
    console.error('Error connecting workspace:', error);
    throw error;
  }
};

export const disconnectWorkspace = () => {
  clearWorkspaceSession();
};

/**
 * Checks if we have an active connection or need auth.
 */
export const hasActiveWorkspaceConnection = (): boolean => {
  return getCachedToken() !== null;
};

export const getWorkspaceSessionInfo = () => {
  const token = getCachedToken();
  const email = getConnectedUserEmail();
  let expiresAt: number | null = null;
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY) || localStorage.getItem(SESSION_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      expiresAt = parsed.expiresAt || null;
    }
  } catch (_) {}

  return {
    isConnected: token !== null,
    email: email || auth.currentUser?.email || null,
    expiresAt
  };
};

/**
 * Helper to make verified requests to Google APIs.
 */
async function googleFetch(url: string, options: RequestInit = {}): Promise<any> {
  const token = getCachedToken();
  if (!token) {
    throw new Error('Workspace is not connected. Please connect your Google account.');
  }

  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  let response: Response;
  try {
    response = await fetch(url, { ...options, headers });
  } catch (netErr: any) {
    console.warn(`[Google Workspace Network Warning] Failed to reach ${url}:`, netErr?.message || netErr);
    throw new Error('Unable to connect to Google API service. Please verify network connectivity or browser cookie permissions.');
  }

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = `Google API Error (${response.status}): ${response.statusText}`;
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.error?.message || errorMessage;
    } catch (_) {}

    // Handle token expiration
    if (response.status === 401) {
      console.warn('[Google Workspace] Token expired or revoked (401). Clearing cached session.');
      clearWorkspaceSession();
      throw new Error('Your Google Workspace session token has expired. Please click "Connect Workspace" to refresh your session.');
    }

    throw new Error(errorMessage);
  }

  if (response.status === 204) {
    return { success: true };
  }

  return response.json();
}

// ==========================================
// Drive Folder Entity Management
// ==========================================

export const ensureEntityDriveFolder = async (companyName: string, entityId?: string): Promise<{ folderId: string; webViewLink?: string }> => {
  const sanitizedName = (companyName || 'Quantum AI Solutions').trim();
  const folderName = `ProcureFlow - ${sanitizedName}`;
  const cacheKey = `${FOLDER_CACHE_KEY_PREFIX}${entityId || sanitizedName.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;

  // Check in local cache first
  const cachedFolderId = localStorage.getItem(cacheKey);
  if (cachedFolderId) {
    try {
      const existing = await googleFetch(`https://www.googleapis.com/drive/v3/files/${cachedFolderId}?fields=id,name,trashed,webViewLink`);
      if (existing && !existing.trashed) {
        return { folderId: existing.id, webViewLink: existing.webViewLink };
      }
    } catch (_) {
      // Cached ID was invalid or deleted; continue to search/create
      localStorage.removeItem(cacheKey);
    }
  }

  try {
    // Search existing folders with matching name
    const query = `name = '${folderName.replace(/'/g, "\\'")}' and mimeType = 'application/vnd.google-apps.folder' and trashed = false`;
    const searchRes = await listDriveFiles(query);
    if (searchRes && searchRes.length > 0) {
      const foundId = searchRes[0].id;
      localStorage.setItem(cacheKey, foundId);
      return { folderId: foundId, webViewLink: searchRes[0].webViewLink };
    }

    // Create new dedicated folder
    const createdFolder = await googleFetch('https://www.googleapis.com/drive/v3/files?fields=id,name,webViewLink', {
      method: 'POST',
      body: JSON.stringify({
        name: folderName,
        mimeType: 'application/vnd.google-apps.folder',
        description: `Official GovCon Proposal & Contract Vault for ${sanitizedName} created by ProcureFlow.`
      })
    });

    if (createdFolder?.id) {
      localStorage.setItem(cacheKey, createdFolder.id);
      return { folderId: createdFolder.id, webViewLink: createdFolder.webViewLink };
    }
  } catch (err) {
    console.warn('Error ensuring entity Drive folder, falling back to root Drive:', err);
  }

  return { folderId: 'root' };
};

// ==========================================
// 1. Google Docs Integration
// ==========================================
export interface DocResponse {
  documentId: string;
  title: string;
  webViewLink?: string;
}

export const createGoogleDoc = async (title: string, bodyText: string, folderId?: string): Promise<DocResponse> => {
  // 1. Create empty document
  const doc = await googleFetch('https://docs.googleapis.com/v1/documents', {
    method: 'POST',
    body: JSON.stringify({ title }),
  });

  const documentId = doc.documentId;

  // 2. Append text content
  if (bodyText) {
    await googleFetch(`https://docs.googleapis.com/v1/documents/${documentId}:batchUpdate`, {
      method: 'POST',
      body: JSON.stringify({
        requests: [
          {
            insertText: {
              text: bodyText,
              location: { index: 1 },
            },
          },
        ],
      }),
    });
  }

  // 3. Move to target folder if specified
  let webViewLink = `https://docs.google.com/document/d/${documentId}/edit`;
  if (folderId && folderId !== 'root') {
    try {
      const driveFile = await googleFetch(`https://www.googleapis.com/drive/v3/files/${documentId}?addParents=${folderId}&fields=id,parents,webViewLink`, {
        method: 'PATCH',
      });
      if (driveFile?.webViewLink) {
        webViewLink = driveFile.webViewLink;
      }
    } catch (e) {
      console.warn('Could not move doc to entity folder:', e);
    }
  }

  return {
    documentId,
    title,
    webViewLink
  };
};

/**
 * Exports the complete 16-document comprehensive proposal package to Google Docs.
 */
export const exportProposalToGoogleDoc = async (
  proposalData: any, 
  entityProfile?: { companyName?: string; id?: string; uei?: string }
): Promise<{ documentId: string; title: string; webViewLink: string }> => {
  const companyName = entityProfile?.companyName || proposalData?.vendorName || 'Enterprise Offeror';
  const folderInfo = await ensureEntityDriveFolder(companyName, entityProfile?.id);

  const docTitle = `[PROPOSAL] ${proposalData.solicitationTitle || 'State Bid'} - ${proposalData.solicitationId || 'RFP'} (${companyName})`;

  const docBody = `
================================================================================
OFFICIAL MULTI-VOLUME STATE & FEDERAL PROPOSAL SUBMISSION
================================================================================

SOLICITATION: ${proposalData.solicitationTitle} (${proposalData.solicitationId})
TARGET AGENCY: ${proposalData.agency} | STATE / JURISDICTION: ${proposalData.state}
OFFEROR / SUBMITTING ENTITY: ${proposalData.vendorName} (UEI: ${proposalData.vendorUei || 'N/A'})
OVERALL COMPLIANCE SCORE: ${proposalData.overallComplianceScore}% | PRICE-TO-WIN RATING: ${proposalData.marketCompetitivenessRating}
SUBMISSION DATE: ${new Date().toLocaleDateString()}

--------------------------------------------------------------------------------
VOLUME I: EXECUTIVE TRANSMITTAL & ADMINISTRATIVE COMPLIANCE
--------------------------------------------------------------------------------

1. OFFICIAL TRANSMITTAL & COVER LETTER:
${proposalData.volume1ExecutiveSummary?.transmittalLetter || 'N/A'}

2. EXECUTIVE SUMMARY & VALUE PROPOSITION:
${proposalData.volume1ExecutiveSummary?.executiveSummary || 'N/A'}

3. PRIME CONTRACTOR PROFILE & REGISTRATIONS:
${proposalData.volume1ExecutiveSummary?.contractorProfile || 'N/A'}

4. KEY PERSONNEL & STAFFING ROSTER:
${proposalData.volume1ExecutiveSummary?.keyPersonnelSummary || 'N/A'}

5. SUBCONTRACTING & HUB / M/WBE PARTICIPATION PLAN:
${proposalData.volume1ExecutiveSummary?.subcontractingHubPlan || 'N/A'}

--------------------------------------------------------------------------------
VOLUME II: TECHNICAL METHODOLOGY & STATEMENT OF WORK (SOW) EXECUTION
--------------------------------------------------------------------------------

1. STATEMENT OF NEED & PROBLEM UNDERSTANDING:
${proposalData.volume2TechnicalApproach?.understandingOfNeed || 'N/A'}

2. TECHNICAL METHODOLOGY & ARCHITECTURE:
${proposalData.volume2TechnicalApproach?.technicalMethodologyAndArchitecture || 'N/A'}

3. WORK BREAKDOWN STRUCTURE (WBS) & SPRINT SCHEDULE:
${proposalData.volume2TechnicalApproach?.workBreakdownStructureAndSprintPlan || 'N/A'}

4. QUALITY ASSURANCE SURVEILLANCE PLAN (QASP) & SLA GUARANTEE:
${proposalData.volume2TechnicalApproach?.qualityAssuranceAndSLACommitment || 'N/A'}

5. CYBERSECURITY CONTROLS, NIST 800-53 / FEDRAMP GOVERNANCE:
${proposalData.volume2TechnicalApproach?.securityComplianceNistSummary || 'N/A'}

6. ENTERPRISE RISK MANAGEMENT & MITIGATION PROTOCOLS:
${proposalData.volume2TechnicalApproach?.riskMitigationPlan || 'N/A'}

--------------------------------------------------------------------------------
VOLUME III: COMPREHENSIVE REQUIREMENTS RESPONSE MATRIX (ATTACHMENT H)
--------------------------------------------------------------------------------
${Array.isArray(proposalData.volume3RequirementsMatrix) ? proposalData.volume3RequirementsMatrix.map((r: any, idx: number) => `
[${idx + 1}] REQ ID: ${r.reqId} | CATEGORY: ${r.category} | COMPLIANCE: ${r.complianceLevel}
REQUIREMENT TEXT: ${r.requirementText}
PROPOSED TECHNICAL SOLUTION: ${r.technicalResponseNarrative}
CROSS-REFERENCE: ${r.crossReference} | SOURCE: ${r.sourceDocument}
--------------------------------------------------------------------------------`).join('\n') : 'Matrix pending.'}

--------------------------------------------------------------------------------
VOLUME IV: COST PROPOSAL, PRICE-TO-WIN & LABOR RATES (ATTACHMENT I)
--------------------------------------------------------------------------------
PROPOSED TOTAL CONTRACT VALUE: $${proposalData.volume4CostProposal?.finalProposedPrice?.toLocaleString() || 'N/A'}
PRICE-TO-WIN SCORE: ${proposalData.volume4CostProposal?.priceToWinScore || 'N/A'} / 100

LABOR RATE CATEGORY SCHEDULE:
${Array.isArray(proposalData.volume4CostProposal?.laborRates) ? proposalData.volume4CostProposal.laborRates.map((l: any) => `
- ROLE: ${l.role} (${l.laborCategory})
  HOURLY RATE: $${l.proposedHourlyRate}/hr (Market Median: $${l.marketMedianRate}/hr)
  HOURS ALLOCATED: ${l.proposedHours} hrs | EXTENDED COST: $${l.extendedCost?.toLocaleString()}
  JUSTIFICATION: ${l.justification}`).join('\n') : 'Labor schedule pending.'}

--------------------------------------------------------------------------------
VOLUME V: LEGAL CERTIFICATIONS, AFFIDAVITS & REDLINES
--------------------------------------------------------------------------------

1. NON-COLLUSION & DEBARMENT AFFIDAVIT:
${proposalData.volume5LegalAndForms?.nonCollusionAffidavitText || 'N/A'}

2. OFFEROR MANDATORY CERTIFICATIONS & COMPLIANCE:
${proposalData.volume5LegalAndForms?.offerorCertificationsSummary || 'N/A'}

--------------------------------------------------------------------------------
VOLUME VI: PRE-SUBMISSION PACKAGING & COMPLIANCE VERIFICATION CHECKLIST
--------------------------------------------------------------------------------
${Array.isArray(proposalData.volume6SubmissionChecklist) ? proposalData.volume6SubmissionChecklist.map((s: any) => `
[${s.status?.toUpperCase() || 'PENDING'}] STEP ${s.step}: ${s.requirement}
NOTES: ${s.notes}`).join('\n') : 'Checklist generated.'}

================================================================================
Generated via ProcureFlow AI Sourcing & Proposal Studio
Synchronized to Google Drive: ${companyName}
================================================================================
`;

  const createdDoc = await createGoogleDoc(docTitle, docBody, folderInfo.folderId);
  return {
    documentId: createdDoc.documentId,
    title: docTitle,
    webViewLink: createdDoc.webViewLink || `https://docs.google.com/document/d/${createdDoc.documentId}/edit`
  };
};

/**
 * Exports a specific Volume or Section to a dedicated Google Document.
 */
export const exportVolumeToGoogleDoc = async (
  volumeTitle: string,
  content: string,
  proposalData: any,
  entityProfile?: { companyName?: string; id?: string }
): Promise<{ documentId: string; title: string; webViewLink: string }> => {
  const companyName = entityProfile?.companyName || proposalData?.vendorName || 'Enterprise Offeror';
  const folderInfo = await ensureEntityDriveFolder(companyName, entityProfile?.id);
  const docTitle = `[${volumeTitle}] ${proposalData?.solicitationId || 'RFX'} - ${companyName}`;

  const header = `
================================================================================
${volumeTitle.toUpperCase()}
SOLICITATION: ${proposalData?.solicitationTitle} (${proposalData?.solicitationId})
OFFEROR: ${companyName}
DATE: ${new Date().toLocaleDateString()}
================================================================================

`;

  const created = await createGoogleDoc(docTitle, header + content, folderInfo.folderId);
  return {
    documentId: created.documentId,
    title: docTitle,
    webViewLink: created.webViewLink || `https://docs.google.com/document/d/${created.documentId}/edit`
  };
};

// ==========================================
// 2. Google Forms Integration
// ==========================================
export const createGoogleForm = async (
  title: string, 
  description: string, 
  questions: Array<{ title: string; type: 'TEXT' | 'CHOICE'; options?: string[] }>
): Promise<any> => {
  // 1. Create a blank form (Forms API v1)
  const form = await googleFetch('https://forms.googleapis.com/v1/forms', {
    method: 'POST',
    body: JSON.stringify({ info: { title, documentTitle: title } }),
  });

  const formId = form.formId;

  // 2. Configure description and questions
  const requests: any[] = [];
  
  // Add description
  requests.push({
    updateFormInfo: {
      info: { description },
      updateMask: 'description'
    }
  });

  // Add questions
  questions.forEach((q, index) => {
    const item: any = {
      title: q.title,
      questionItem: {
        question: {
          required: true,
        }
      }
    };

    if (q.type === 'TEXT') {
      item.questionItem.question.textQuestion = {};
    } else {
      item.questionItem.question.choiceQuestion = {
        type: 'RADIO',
        options: (q.options || ['Yes', 'No', 'N/A']).map(opt => ({ value: opt }))
      };
    }

    requests.push({
      createItem: {
        item,
        location: { index }
      }
    });
  });

  await googleFetch(`https://forms.googleapis.com/v1/forms/${formId}:batchUpdate`, {
    method: 'POST',
    body: JSON.stringify({ requests }),
  });

  return form;
};

// ==========================================
// 3. Google Sheets Integration
// ==========================================
export const createGoogleSheet = async (
  title: string, 
  headers: string[], 
  rows: any[][], 
  folderId?: string
): Promise<any> => {
  // 1. Create spreadsheet
  const sheet = await googleFetch('https://sheets.googleapis.com/v4/spreadsheets', {
    method: 'POST',
    body: JSON.stringify({
      properties: { title }
    })
  });

  const spreadsheetId = sheet.spreadsheetId;

  // 2. Write headers and rows
  const values = [headers, ...rows];
  await googleFetch(`https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/Sheet1!A1:append?valueInputOption=RAW`, {
    method: 'POST',
    body: JSON.stringify({
      values
    })
  });

  // 3. Move to folder if specified
  let webViewLink = `https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit`;
  if (folderId && folderId !== 'root') {
    try {
      const driveFile = await googleFetch(`https://www.googleapis.com/drive/v3/files/${spreadsheetId}?addParents=${folderId}&fields=id,parents,webViewLink`, {
        method: 'PATCH',
      });
      if (driveFile?.webViewLink) {
        webViewLink = driveFile.webViewLink;
      }
    } catch (_) {}
  }

  return { ...sheet, webViewLink };
};

/**
 * Exports Volume III Requirements Matrix to Google Sheets.
 */
export const exportRequirementsToGoogleSheet = async (
  proposalData: any,
  entityProfile?: { companyName?: string; id?: string }
): Promise<{ spreadsheetId: string; title: string; webViewLink: string }> => {
  const companyName = entityProfile?.companyName || proposalData?.vendorName || 'Enterprise Offeror';
  const folderInfo = await ensureEntityDriveFolder(companyName, entityProfile?.id);
  const sheetTitle = `[Att. H Requirements Matrix] ${proposalData.solicitationId || 'RFX'} - ${companyName}`;

  const headers = [
    'Req ID',
    'Category',
    'Requirement Text',
    'Source Document',
    'Compliance Level',
    'Technical Response Narrative',
    'Cross-Reference Section'
  ];

  const rows = (proposalData.volume3RequirementsMatrix || []).map((r: any) => [
    r.reqId || '',
    r.category || '',
    r.requirementText || '',
    r.sourceDocument || '',
    r.complianceLevel || 'Compliant',
    r.technicalResponseNarrative || '',
    r.crossReference || ''
  ]);

  const sheet = await createGoogleSheet(sheetTitle, headers, rows, folderInfo.folderId);
  return {
    spreadsheetId: sheet.spreadsheetId,
    title: sheetTitle,
    webViewLink: sheet.webViewLink || `https://docs.google.com/spreadsheets/d/${sheet.spreadsheetId}/edit`
  };
};

/**
 * Exports Volume IV Cost Proposal & Price-to-Win Labor Rates to Google Sheets.
 */
export const exportLaborRatesToGoogleSheet = async (
  proposalData: any,
  entityProfile?: { companyName?: string; id?: string }
): Promise<{ spreadsheetId: string; title: string; webViewLink: string }> => {
  const companyName = entityProfile?.companyName || proposalData?.vendorName || 'Enterprise Offeror';
  const folderInfo = await ensureEntityDriveFolder(companyName, entityProfile?.id);
  const sheetTitle = `[Att. I Pricing & Labor Rates] ${proposalData.solicitationId || 'RFX'} - ${companyName}`;

  const headers = [
    'Role / Title',
    'Labor Category',
    'Market Median Rate ($/hr)',
    'Proposed Hourly Rate ($/hr)',
    'Proposed Hours',
    'Extended Price ($)',
    'Technical Justification'
  ];

  const rows = (proposalData.volume4CostProposal?.laborRates || []).map((l: any) => [
    l.role || '',
    l.laborCategory || '',
    `$${l.marketMedianRate || 0}`,
    `$${l.proposedHourlyRate || 0}`,
    l.proposedHours || 0,
    `$${(l.extendedCost || 0).toLocaleString()}`,
    l.justification || ''
  ]);

  // Add Summary Row
  rows.push([
    'TOTAL PROPOSED CONTRACT PRICE',
    'ALL CATEGORIES',
    '-',
    '-',
    '-',
    `$${(proposalData.volume4CostProposal?.finalProposedPrice || 0).toLocaleString()}`,
    `Price-to-Win Score: ${proposalData.volume4CostProposal?.priceToWinScore || 0}/100`
  ]);

  const sheet = await createGoogleSheet(sheetTitle, headers, rows, folderInfo.folderId);
  return {
    spreadsheetId: sheet.spreadsheetId,
    title: sheetTitle,
    webViewLink: sheet.webViewLink || `https://docs.google.com/spreadsheets/d/${sheet.spreadsheetId}/edit`
  };
};

// ==========================================
// 4. Google Tasks Integration
// ==========================================
export interface GoogleTask {
  id: string;
  title: string;
  notes?: string;
  due?: string;
  status: 'needsAction' | 'completed';
}

export const getGoogleTasks = async (): Promise<GoogleTask[]> => {
  try {
    const data = await googleFetch('https://tasks.googleapis.com/v1/lists/@default/tasks?maxResults=100');
    return data.items || [];
  } catch (err: any) {
    if (err.message?.includes('Not Found') || err.message?.includes('not enabled') || err.message?.includes('disabled')) {
      console.warn('[Google Tasks] Tasks list uninitialized or not enabled for user:', err.message);
      return [];
    }
    throw err;
  }
};

export const createGoogleTask = async (title: string, notes?: string, due?: string): Promise<GoogleTask> => {
  const body: any = { title, status: 'needsAction' };
  if (notes) body.notes = notes;
  if (due) body.due = due; // Must be RFC 3339 formatted: YYYY-MM-DDTHH:MM:SSZ

  return googleFetch('https://tasks.googleapis.com/v1/lists/@default/tasks', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export const toggleGoogleTaskStatus = async (taskId: string, currentStatus: string): Promise<any> => {
  const newStatus = currentStatus === 'completed' ? 'needsAction' : 'completed';
  const body: any = { status: newStatus };
  if (newStatus === 'needsAction') {
    body.completed = null; // Clear completion timestamp
  }
  return googleFetch(`https://tasks.googleapis.com/v1/lists/@default/tasks/${taskId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
};

/**
 * Pushes the 16-step proposal submission checklist into Google Tasks.
 */
export const syncProposalChecklistToGoogleTasks = async (proposalData: any): Promise<GoogleTask[]> => {
  const checklist = proposalData.volume6SubmissionChecklist || [];
  const createdTasks: GoogleTask[] = [];

  for (const item of checklist) {
    try {
      const task = await createGoogleTask(
        `[${proposalData.solicitationId || 'RFX'}] Step ${item.step}: ${item.requirement}`,
        `Requirement: ${item.requirement}\nStatus: ${item.status}\nGuidance: ${item.notes}\nSolicitation: ${proposalData.solicitationTitle} (${proposalData.agency})`
      );
      createdTasks.push(task);
    } catch (e) {
      console.warn('Error pushing task:', e);
    }
  }

  return createdTasks;
};

// ==========================================
// 5. Contacts (People API) Integration
// ==========================================
export interface GoogleContact {
  resourceName: string;
  name: string;
  email: string;
  phone?: string;
}

export const getGoogleContacts = async (): Promise<GoogleContact[]> => {
  try {
    const data = await googleFetch(
      'https://people.googleapis.com/v1/people/me/connections?personFields=names,emailAddresses,phoneNumbers&pageSize=100'
    );

    const connections = data.connections || [];
    return connections.map((c: any) => {
      const name = c.names?.[0]?.displayName || 'Unnamed Contact';
      const email = c.emailAddresses?.[0]?.value || '';
      const phone = c.phoneNumbers?.[0]?.value || '';
      return {
        resourceName: c.resourceName,
        name,
        email,
        phone
      };
    }).filter((contact: GoogleContact) => contact.email !== '');
  } catch (err: any) {
    if (err.message?.includes('Not Found') || err.message?.includes('not enabled') || err.message?.includes('disabled')) {
      console.warn('[Google Contacts] Contacts service returned:', err.message);
      return [];
    }
    throw err;
  }
};

// ==========================================
// 6. Gmail Integration
// ==========================================
export const sendGmailEmail = async (to: string, subject: string, bodyText: string): Promise<any> => {
  // Construct raw RFC 2822 email message encoded in base64url
  const utf8Subject = `=?utf-8?B?${btoa(unescape(encodeURIComponent(subject)))}?=`;
  const emailLines = [
    `To: ${to}`,
    `Subject: ${utf8Subject}`,
    'Content-Type: text/plain; charset=utf-8',
    'MIME-Version: 1.0',
    '',
    bodyText
  ];

  const emailStr = emailLines.join('\r\n');
  const base64Safe = btoa(unescape(encodeURIComponent(emailStr)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

  return googleFetch('https://gmail.googleapis.com/gmail/v1/users/me/messages/send', {
    method: 'POST',
    body: JSON.stringify({
      raw: base64Safe
    })
  });
};

// ==========================================
// 7. Google Drive Integration
// ==========================================
export interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  modifiedTime?: string;
  size?: string;
  webViewLink?: string;
}

export const listDriveFiles = async (q?: string): Promise<DriveFile[]> => {
  let url = 'https://www.googleapis.com/drive/v3/files?fields=files(id,name,mimeType,modifiedTime,size,webViewLink)&orderBy=modifiedTime desc&pageSize=100';
  if (q) {
    url += `&q=${encodeURIComponent(q)}`;
  }
  const data = await googleFetch(url);
  return data.files || [];
};

export const uploadFileToDrive = async (
  fileName: string, 
  mimeType: string, 
  content: string | Blob, 
  parentId?: string
): Promise<DriveFile> => {
  const metadata: any = {
    name: fileName,
    mimeType: mimeType
  };

  if (parentId) {
    metadata.parents = [parentId];
  }

  // Multi-part upload (metadata + media content)
  const boundary = 'foo_bar_boundary';
  const delimiter = `\r\n--${boundary}\r\n`;
  const closeDelimiter = `\r\n--${boundary}--`;

  const bodyContent = typeof content === 'string' ? content : await content.text();

  const multipartBody = 
    delimiter +
    'Content-Type: application/json; charset=UTF-8\r\n\r\n' +
    JSON.stringify(metadata) +
    delimiter +
    `Content-Type: ${mimeType}\r\n\r\n` +
    bodyContent +
    closeDelimiter;

  return googleFetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,mimeType,webViewLink', {
    method: 'POST',
    headers: {
      'Content-Type': `multipart/related; boundary=${boundary}`
    },
    body: multipartBody
  });
};

// ==========================================
// 8. Google Calendar Integration
// ==========================================
export interface CalendarEvent {
  id: string;
  summary: string;
  description?: string;
  start: { dateTime: string };
  end: { dateTime: string };
  htmlLink?: string;
}

export const getCalendarEvents = async (): Promise<CalendarEvent[]> => {
  const now = new Date().toISOString();
  try {
    const data = await googleFetch(
      `https://www.googleapis.com/calendar/v3/calendars/primary/events?timeMin=${now}&orderBy=startTime&singleEvents=true&maxResults=50`
    );
    return data.items || [];
  } catch (err: any) {
    if (
      err.message?.includes('signed up for Google Calendar') ||
      err.message?.includes('notYetRegistered') ||
      err.message?.includes('userNotRegistered') ||
      err.message?.includes('Not Found') ||
      err.message?.includes('404')
    ) {
      console.warn('[Google Calendar] Calendar service is not initialized on this Google account:', err.message);
      return [];
    }
    throw err;
  }
};

export const createCalendarEvent = async (event: {
  summary: string;
  description?: string;
  start: string; // ISO String
  end: string; // ISO String
}): Promise<CalendarEvent> => {
  const body = {
    summary: event.summary,
    description: event.description,
    start: { dateTime: event.start, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone },
    end: { dateTime: event.end, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }
  };

  return googleFetch('https://www.googleapis.com/calendar/v3/calendars/primary/events', {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

/**
 * Syncs proposal submission deadline and pre-submission review milestones to Google Calendar.
 */
export const syncProposalDeadlinesToGoogleCalendar = async (
  solicitation: any,
  proposalData?: any
): Promise<CalendarEvent[]> => {
  const events: CalendarEvent[] = [];
  const dueDateStr = solicitation?.dueDate || proposalData?.dueDate;

  if (dueDateStr) {
    const dueTime = new Date(dueDateStr);
    if (!isNaN(dueTime.getTime())) {
      // 1. Official Submission Deadline Event
      const deadlineStart = new Date(dueTime);
      deadlineStart.setHours(deadlineStart.getHours() - 1);
      const deadlineEvent = await createCalendarEvent({
        summary: `🚨 DEADLINE: Submit Proposal for ${solicitation.title || 'RFX'} (${solicitation.solicitationId || 'RFX'})`,
        description: `Official state proposal closing date.\nAgency: ${solicitation.agency || 'State Dept'}\nState: ${solicitation.state || 'TX'}\nValue: $${solicitation.pricing?.toLocaleString() || 'N/A'}\nEnsure all 16 compliance volumes are packaged and digitally signed.`,
        start: deadlineStart.toISOString(),
        end: dueTime.toISOString()
      });
      events.push(deadlineEvent);

      // 2. Final Red Team Review Milestone (2 days prior)
      const reviewStart = new Date(dueTime);
      reviewStart.setDate(reviewStart.getDate() - 2);
      reviewStart.setHours(10, 0, 0, 0);
      const reviewEnd = new Date(reviewStart);
      reviewEnd.setHours(12, 0, 0, 0);
      
      if (reviewStart.getTime() > Date.now()) {
        const reviewEvent = await createCalendarEvent({
          summary: `🛡️ Red Team Compliance Gate: ${solicitation.solicitationId || 'RFX'} Proposal Review`,
          description: `Internal Executive Review of Attachment H compliance matrix, labor rates, and non-collusion certifications for ${solicitation.title}.`,
          start: reviewStart.toISOString(),
          end: reviewEnd.toISOString()
        });
        events.push(reviewEvent);
      }
    }
  }

  return events;
};

